import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Badge,
  Box,
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Button,
  Divider,
  Tooltip,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DescriptionIcon from '@mui/icons-material/Description';
import BuildIcon from '@mui/icons-material/Build';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import EventIcon from '@mui/icons-material/Event';
import moment from 'moment';
import Store from '../utilities/store';
import history from '../utilities/history';
import { StateManager } from '../utilities/stateManager';

// Per-type icon + accent color for the notification rows.
const TYPE_META = {
  deposit:         { Icon: AttachMoneyIcon,   color: '#2e7d32' },
  load_assigned:   { Icon: LocalShippingIcon, color: '#1565c0' },
  load_date:       { Icon: LocalShippingIcon, color: '#e65100' },
  title_arrived:   { Icon: DescriptionIcon,   color: '#6a1b9a' },
  so_items_added:  { Icon: BuildIcon,         color: '#00695c' },
  so_status:       { Icon: FactCheckIcon,     color: '#023e84' },
  so_part_ordered: { Icon: Inventory2Icon,    color: '#ad1457' },
  so_part_arrived: { Icon: Inventory2Icon,    color: '#2e7d32' },
  lead_follow_up:  { Icon: EventIcon,         color: '#023e84' },
  price_review:    { Icon: TrendingDownIcon,  color: '#ad1457' },
  feed_failure:    { Icon: CloudOffIcon,      color: '#c62828' },
  feed_recovered:  { Icon: CloudDoneIcon,     color: '#2e7d32' },
  inventory_email_draft: { Icon: MarkEmailReadIcon, color: '#023e84' },
};

// What each row opens, and how it's labelled on the row so the click is
// predictable before you make it.
const TARGET_KINDS = {
  so:   { label: (id) => id },
  car:  { label: (id) => `SN ${id}` },
  load: { label: () => 'load' },
  // A whole-page destination rather than a single record — the notification's
  // `id` is the route to push. Used by digests that summarize many cars (a
  // staged price strategy run) and by alerts about a system rather than a car
  // (e.g. '/feed-health').
  page: { label: () => 'open' },
};

// Types whose destination is one fixed page, where the link stored on the doc
// can't be trusted. `inventory_email_draft` shipped pointing at '/inventory' —
// the car list, which is also the admin home page, so clicking it did nothing
// visible and never showed the draft. Overriding by type repairs the
// notifications already sitting in the tray without a data migration.
const TYPE_PAGE_OVERRIDES = {
  inventory_email_draft: '/inventory-email',
};

// Resolve what a notification opens. A fixed-page type wins first (see above).
// Then an explicit `link` — it's what the trigger that wrote the notification
// meant. Failing that (an older doc, or a kind this build doesn't know), fall
// back to the notification's `data`, most specific first: the service order,
// then the car it's on, then the load. Returns null when there's genuinely
// nothing to open.
function resolveTarget(n) {
  const override = n && TYPE_PAGE_OVERRIDES[n.type];
  if (override) return { kind: 'page', id: override };

  const link = n && n.link;
  if (link && link.id && TARGET_KINDS[link.kind]) {
    return { kind: link.kind, id: String(link.id) };
  }
  const data = (n && n.data) || {};
  if (data.orderId) return { kind: 'so', id: String(data.orderId) };
  if (data.stock) return { kind: 'car', id: String(data.stock) };
  if (data.loadId) return { kind: 'load', id: String(data.loadId) };
  return null;
}

// Bell + badge in the header. Live-subscribes to the current user's
// notifications, chimes on new arrivals (mutable, persisted), and deep-links to
// the related car/load on click. Mirrors ProposedHoursButton's chime pattern.
export default function NotificationTray() {
  const [anchor, setAnchor] = useState(null);
  const [items, setItems] = useState([]);
  const [muted, setMuted] = useState(() => localStorage.getItem('notificationChimeMuted') === 'true');
  const mutedRef = useRef(muted);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  const audioCtxRef = useRef(null);

  // Short two-tone chime via WebAudio (no asset needed), distinct from the
  // proposed-hours ascending triad.
  const playChime = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const start = ctx.currentTime;
      [880, 1174.66].forEach((freq, i) => { // A5 → D6
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const t = start + i * 0.12;
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.22, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.35);
      });
    } catch (e) {
      // Audio blocked / unavailable — fail silently.
    }
  }, []);

  useEffect(() => {
    const unsub = Store.subscribeNotifications(
      setItems,
      (added) => {
        if (!mutedRef.current) playChime();
        StateManager.setAlertAndOpen(
          `${added} new notification${added > 1 ? 's' : ''}`,
          'info',
        );
      },
    );
    return () => unsub && unsub();
  }, [playChime]);

  const unreadCount = items.filter(n => !n.read).length;

  const open = (e) => setAnchor(e.currentTarget);
  const close = () => setAnchor(null);

  const toggleMuted = (e) => {
    e.stopPropagation();
    setMuted(prev => {
      const next = !prev;
      localStorage.setItem('notificationChimeMuted', String(next));
      // Unmuting is a user gesture — confirm with a chime, which also unlocks
      // the AudioContext so future chimes can play.
      if (!next) playChime();
      return next;
    });
  };

  const openTarget = (target) => {
    if (!target) return;
    if (target.kind === 'car') StateManager.openCarModal(target.id);
    else if (target.kind === 'load') StateManager.openLoadModal(target.id);
    else if (target.kind === 'so') StateManager.openServiceOrderModal(target.id);
    else if (target.kind === 'page') history.push(target.id);
  };

  const handleRowClick = (n) => {
    if (!n.read) Store.markNotificationRead(n.id).catch(() => {});
    openTarget(resolveTarget(n));
    close();
  };

  const markAllRead = () => {
    Store.markNotificationsRead(items.filter(n => !n.read).map(n => n.id)).catch(() => {});
  };

  return (
    <>
      <IconButton color="inherit" onClick={open} size="large">
        <Badge badgeContent={unreadCount} color="error" max={99}
          sx={{ '& .MuiBadge-badge': { fontWeight: 700 } }}>
          {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380, maxHeight: 'calc(100vh - 100px)' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notifications</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={muted ? 'Chime muted — click to unmute' : 'Chime on new — click to mute'}>
              <IconButton size="small" onClick={toggleMuted} color={muted ? 'default' : 'primary'}>
                {muted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Mark all as read">
              <span>
                <IconButton size="small" onClick={markAllRead} disabled={unreadCount === 0}>
                  <DoneAllIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Divider />

        {items.length === 0 ? (
          <Box sx={{ px: 3, py: 5, textAlign: 'center', color: 'text.secondary' }}>
            <NotificationsNoneIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
            <Typography variant="body2">You're all caught up</Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ overflowY: 'auto' }}>
            {items.map(n => {
              const meta = TYPE_META[n.type] || { Icon: NotificationsIcon, color: '#757575' };
              const Icon = meta.Icon;
              const target = resolveTarget(n);
              return (
                <ListItemButton
                  key={n.id}
                  onClick={() => handleRowClick(n)}
                  sx={{
                    alignItems: 'flex-start',
                    backgroundColor: n.read ? 'transparent' : 'rgba(2, 62, 132, 0.06)',
                    borderLeft: n.read ? '3px solid transparent' : `3px solid ${meta.color}`,
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 44 }}>
                    <Avatar sx={{ bgcolor: meta.color, width: 32, height: 32 }}>
                      <Icon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={n.title}
                    secondary={
                      <>
                        {n.body}
                        {n.createdAt ? (
                          <Typography component="span" variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                            {moment.unix(n.createdAt).fromNow()}
                          </Typography>
                        ) : null}
                      </>
                    }
                    primaryTypographyProps={{ fontWeight: n.read ? 500 : 700, fontSize: '0.875rem' }}
                    secondaryTypographyProps={{ component: 'span', fontSize: '0.8rem' }}
                  />
                  {/* Says what the row opens, so the click is predictable. */}
                  {target && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, pt: 0.5, pl: 0.5, flexShrink: 0, color: 'text.secondary' }}>
                      <Typography variant="caption" noWrap sx={{ fontWeight: 600 }}>
                        {TARGET_KINDS[target.kind].label(target.id)}
                      </Typography>
                      <ChevronRightIcon fontSize="small" />
                    </Box>
                  )}
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
}
